import React, { memo, useRef, useState } from "react";
import styled from "styled-components";
import locationIcon from "./assets/location.svg";
import ideaIcon from "./assets/idea.svg";
import domainIcon from "./assets/domain.svg";
import editIcon from "./assets/edit.svg";
import shareIcon from "./assets/share.svg";
import plusIcon from "./assets/add.svg";
import { handleFollowUnfollow } from "../components/Startups/StartupShow";
import ContainerButton from "./ContainerButton";
import useStartupStore from "../stores/startupStore";
import ContactMessage from "../components/Startups/ContactMessage";
import Breadcrumbs from "./Breadcrumbs";
import fetchApi from "../components/Lib/api";
import { useTranslation } from "react-i18next";
import StartupMembers from "./StartupMembers";
import useLayoutStore from "../stores/useLayoutStore";
import { Indicator, Tooltip, Loader, Menu } from "@mantine/core";
import { IconPencil, IconDotsVertical, IconUsers, IconMessage, IconCalendar, IconUserPlus } from "@tabler/icons-react";
import Mixpanel from "../components/Lib/mixpanel";
import { IconCopyPlus } from "@tabler/icons-react";
import { InviteMembers } from "../components/Startups/InviteMember";
import Meeting from "../components/Startups/Meeting";
import ChatComponent from "../components/Startups/StartupChat";
import { useDisclosure } from "@mantine/hooks";
import { Modal, Button, Select, Text, Group, ActionIcon, Stack, TextInput, Textarea, MultiSelect, Card, Avatar, Box, ScrollArea, Divider, Grid, Badge, Tabs } from "@mantine/core";
import { IconDoor, IconUsersGroup, IconCheck, IconX, IconSearch, IconChecklist } from "@tabler/icons-react";
import useUserStore from "../stores/userStore";
import Notification from "../components/Lib/Notification";
import { TimePickerComponent, DatePickerComponent } from "@syncfusion/ej2-react-calendars";
const MemoContactMessage = memo(ContactMessage);

// Create a context to manage modal state
const ModalContext = React.createContext();

const ModalProvider = ({ children }) => {
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);
  
  const openModal = () => setIsAnyModalOpen(true);
  const closeModal = () => setIsAnyModalOpen(false);
  
  return (
    <ModalContext.Provider value={{ isAnyModalOpen, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
};

// Custom hook to access modal context
const useModalContext = () => React.useContext(ModalContext);

// Wrapper component to trigger modals from menu items
const ModalTrigger = ({ children, onClick }) => {
  const { openModal } = useModalContext();
  
  const handleClick = (e) => {
    openModal();
    if (onClick) onClick(e);
  };
  
  return React.cloneElement(children, { onClick: handleClick });
};

const StartupHeaderContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: 20px;
  align-items: flex-start;
  border-radius: 8px;

  @media (max-width: 1024px) {
    grid-gap: 10px;
  }
`;

const CompactHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid var(--color-container-card-default);
`;

const CompactLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CompactAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--color-container-card-default);
  flex-shrink: 0;
  cursor: pointer;
`;

const CompactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const CompactName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-background-card);
  margin: 0;
  line-height: 1.2;
`;

const CompactMeta = styled.span`
  font-size: 13px;
  color: var(--color-background-main);
  opacity: 0.8;
`;

const CompactDescription = styled.p`
  font-size: 14px;
  color: var(--color-background-main);
  margin: 0;
  line-height: 1.4;
  opacity: 0.9;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: var(--color-button-accent);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CompactActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
`;

const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FollowButton = styled.button`
  background: var(--color-button-accent);
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const MenuButton = styled.button`
  background: var(--color-container-card-default);
  border: none;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--color-container-card-hover);
  }
`;

const CompactMembers = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MembersLabel = styled.span`
  font-size: 12px;
  color: var(--color-background-main);
  opacity: 0.7;
  font-weight: 500;
`;

const MembersAvatars = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const MemberAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--color-container-card-default);
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const MemberInitials = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-container-card-default);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-background-main);
  border: 1px solid var(--color-container-card-default);
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const CompactNameLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CompactLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--color-text-secondary);
  opacity: 0.8;
  font-weight: 400;
`;

const LocationIcon = styled.img`
  width: 12px;
  height: 12px;
  opacity: 0.7;
`;

const MoreMembers = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--color-button-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const StartupWrapper = styled.div`
  grid-column: 1 / span 6;
  display: flex;
`;

const StartupImage = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 6px;
  object-fit: cover;
  margin-right: 16px;
  cursor: pointer;
  border: 1px solid var(--color-container-card-default);
`;

const StartupContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  margin-left: 8px;
  align-items: flex-start;
`;

const StartupTitle = styled.h2`
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-body-2);
  color: var(--color-background-card);
  line-height: 26px;
`;

const StartupDescription = styled.span`
  text-align: left;
  margin-right: 8px;
  color: var(--color-background-main);
`;

const ReadMoreButton = styled.button`
  color: var(--color-button-accent);
  font-weight: var(--font-weight-body-3);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: inline;
`;

const StartupInfoHead = styled.div`
  grid-column: 7 / span 3;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  font-size: var(--font-size-body-2);
  color: var(--color-background-main);
  gap: 12px;
`;

const InfoItemLeft = styled.div`
  display: flex;
  gap: 8px;
  font-size: var(--font-size-body-4);
  color: var(--color-background-card);
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const TeamButton = styled.div`
  grid-column: 10 / span 3;
  height: 100%;
`;

const ActionButton = styled.button`
  background-color: var(--color-container-card-default);
  border: none;
  border-radius: 4px;
  padding: 7px;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  img {
    width: 15px;
    height: 15px;
  }
`;

const StartupHeader = () => {
  const { isToggle } = useLayoutStore();
  const { t } = useTranslation();

  const { startup, slug, isOwner, role, isMember, isFollowing, isSubmitted, setIsSubmitted, setSubmittedAt, members, entity } =
    useStartupStore((state) => ({
      startup: state.startup,
      role: state.role,
      isFollowing: state.isFollowing,
      startupImage: state.startupImage,
      slug: state.slug,
      isOwner: state.isOwner,
      isMember: state.isMember,
      isSubmitted: state.isSubmitted,
      setIsSubmitted: state.setIsSubmitted,
      setSubmittedAt: state.submittedAt,
      members: state.members,
      entity: state.entity
    }));

  const { user: currentUser } = useUserStore((state) => state.user);

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [follow, setFollow] = useState(isFollowing);
  const fileInputRef = useRef(null);
  const [startupImage, setStartupImage] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [submitProjectOpened, { open: openSubmitProject, close: closeSubmitProject }] = useDisclosure(false);
  const [projectSubmitLoading, setProjectSubmitLoading] = useState(false);
  const [submitValue, setSubmitValue] = useState("");
  const [submitData, setSubmitData] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedSubmitUser, setSelectedSubmitUser] = useState(null);
  const [submitProfessors, setSubmitProfessors] = useState([]);
  const [manageTeamOpened, { open: openManageTeam, close: closeManageTeam }] = useDisclosure(false);
  const [leaveProjectOpened, { open: openLeaveProject, close: closeLeaveProject }] = useDisclosure(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState("");
  const [notification, setNotification] = useState(null);
  const [meetingOpened, { open: openMeeting, close: closeMeeting }] = useDisclosure(false);
  const [teamSearch, setTeamSearch] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [meetingDate, setMeetingDate] = useState(null);
  const [startDateTime, setStartDateTime] = useState(null);
  const [selectedValues, setSelectedValues] = useState([]);
  const [meetingNotification, setMeetingNotification] = useState(null);
  const endTimeRef = useRef(null);
  const [users, setUsers] = useState({
    pending_requests: [],
    not_pending_requests: [],
  });
  const [loading, setLoading] = useState(false);

  const handleRole = async (membershipId, newRoleIndex, userToUpdate) => {
    const memberToUpdate = userToUpdate;
    const startTime = performance.now();
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/update_role`,
        "PUT",
        { membership_id: membershipId, new_role: newRoleIndex }
      );

      const responseTime = Math.round(performance.now() - startTime);
      if (response.ok) {
        const res = await response.json();
        setNotification({ type: "success", content: res.message });
        // Update local state so the UI reflects the new role immediately
        setUsers((prev) => ({
          ...prev,
          not_pending_requests: prev.not_pending_requests.map((item) => {
            const u = item.user;
            if (u.membership_id === membershipId) {
              return { ...item, user: { ...u, role: newRoleIndex } };
            }
            return item;
          }),
        }));
        Mixpanel.settings('Member Role', 'Updated', {
          member_id: memberToUpdate ? memberToUpdate.id : 'unknown',
          old_value: memberToUpdate ? memberToUpdate.role : 'unknown',
          new_value: newRoleIndex,
          response_time_ms: responseTime,
          startup_slug: slug
        });
      } else {
        const { error } = await response.json();
        setNotification({ type: "failure", content: error });
        Mixpanel.error('Member Role Update Error', {
          error_message: error,
          error_type: 'API Error',
          http_status: response.status,
          component: 'Manage Team Modal',
          user_action: 'Update Member Role',
          member_id: memberToUpdate ? memberToUpdate.id : 'unknown',
          response_time_ms: responseTime,
          startup_slug: slug
        });
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      Mixpanel.error('Member Role Update Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'Manage Team Modal',
        user_action: 'Update Member Role',
        member_id: userToUpdate ? userToUpdate.id : 'unknown',
        response_time_ms: responseTime,
        startup_slug: slug
      });
    }
  };

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem("startupHeaderDetailsOpen");
      if (saved === "true") setIsDetailsOpen(true);
    } catch (_) {}
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem("startupHeaderDetailsOpen", isDetailsOpen ? "true" : "false");
    } catch (_) {}
  }, [isDetailsOpen]);

  // Fetch professors when submit modal opens
  React.useEffect(() => {
    if (!submitProjectOpened) return;
    
    const fetchProfessors = async () => {
      const response = await fetchApi(
        `/startup_entities/${slug}/join_requests/pending_requests_for_startup`,
        "GET"
      );
      if (response.ok) {
        const data = await response.json();
        const profs = data.not_pending_requests
          .filter((obj) => obj.user.role === 5)
          .map((obj) => obj.user);
        setSubmitProfessors(profs);
      }
    };
    fetchProfessors();
  }, [submitProjectOpened, slug]);

  const fetchTeamData = async () => {
    Mixpanel.custom('Manage Team Button Clicked', { startup_slug: slug });
    setLoading(true);
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/join_requests/pending_requests_for_startup`,
        "GET"
      );
      if (response.ok) {
        const res = await response.json();
        setUsers(res);
        openManageTeam();
      } else {
        const msg = (await response.json()).error;
        console.error(msg);
      }
    } catch (error) {
      console.error("Error fetching team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (accepted, index) => {
    const startTime = performance.now();
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/join_requests/${
          users.pending_requests[index].join_request_id
        }/${accepted ? "accept" : "reject"}_request`,
        "PATCH"
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (response.ok) {
        const res = await response.json();
        setNotification({ type: "success", content: res.message });
        if (accepted) {
          setUsers((u) => ({
            not_pending_requests: [
              ...u.not_pending_requests,
              u.pending_requests[index],
            ],
            pending_requests: u.pending_requests.filter((_, i) => i !== index),
          }));
        } else {
          setUsers((u) => ({
            ...u,
            pending_requests: u.pending_requests.filter((_, i) => i !== index),
          }));
        }
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });
      }
    } catch (error) {
      console.error("Error handling request:", error);
    }
  };

  const memberNames = members.map((mbr) => ({
    value: mbr.id,
    label: mbr.first_name + " " + mbr.last_name,
  }));

  const handleDateChange = (args) => {
    setMeetingDate(args.value);
    setStartDateTime(null);
    if (endTimeRef.current) {
      endTimeRef.current.value = null;
    }
  };

  const handleStartTimeChange = (args) => {
    if (args.value) {
      const selectedDate = new Date(meetingDate);
      selectedDate.setHours(args.value.getHours(), args.value.getMinutes(), 0);
      setStartDateTime(selectedDate);
      if (endTimeRef.current) {
        endTimeRef.current.value = null;
        endTimeRef.current.min = selectedDate;
      }
    }
  };

  const resetMeetingForm = () => {
    setTitle("");
    setDescription("");
    setMeetingDate(null);
    setStartDateTime(null);
    setSelectedValues([]);
    if (endTimeRef.current) {
      endTimeRef.current.value = null;
      endTimeRef.current.min = null;
    }
  };

  const handleSchedule = async () => {
    const startTime = performance.now();
    try {
      const response = await fetchApi("/meetings", "POST", {
        slug: slug,
        meeting: {
          title,
          description,
          start_time: startDateTime,
          end_time: endTimeRef.current.value,
          meeting_date: meetingDate,
        },
        attendees: selectedValues,
      });

      const responseTime = Math.round(performance.now() - startTime);

      if (response.ok) {
        const res = await response.json();
        setMeetingNotification({ type: "success", content: res.message });
        Mixpanel.content('Meeting', 'Created', {
          creation_method: (isMember || isOwner) ? 'Schedule' : 'Request',
          meeting_title: title,
          attendees_count: selectedValues.length,
          response_time_ms: responseTime,
          startup_slug: slug,
        });
      } else {
        const msg = (await response.json()).error;
        setMeetingNotification({ type: "failure", content: msg });
        Mixpanel.error('Meeting Schedule/Request Error', {
          error_message: msg,
          error_type: 'API Error',
          http_status: response.status,
          component: 'Meeting Modal',
          user_action: (isMember || isOwner) ? 'Schedule Meeting' : 'Request Meeting',
          response_time_ms: responseTime,
          startup_slug: slug,
        });
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error during meeting creation:", error);
      Mixpanel.error('Meeting Schedule/Request Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'Meeting Modal',
        user_action: (isMember || isOwner) ? 'Schedule Meeting' : 'Request Meeting',
        response_time_ms: responseTime,
        startup_slug: slug,
      });
    } finally {
      resetMeetingForm();
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    Mixpanel.button("Startup Description Toggle", {
      action: isCollapsed ? "Read More" : "Read Less",
      startup_slug: slug,
      current_state: isCollapsed ? "collapsed" : "expanded",
    });
  };

  const truncatedDescription = isCollapsed
    ? startup.description.slice(0, 100) + "..."
    : startup.description;

  // Handle image click event
  const handleImageClick = () => {
    if (isMember || isOwner) {
      Mixpanel.button("Edit Startup Image Button", {
        category: "Profile Management",
        location: "Startup Header",
        startup_slug: slug,
        user_role: isOwner ? "owner" : "member",
      });
      fileInputRef.current.click();
    } else {
      Mixpanel.button("Edit Startup Image Button", {
        category: "Profile Management",
        location: "Startup Header",
        action: "Attempted without permission",
        startup_slug: slug,
        user_role: role,
      });
    }
  };

  const handleFileChange = async (event) => {
    // Handle file changes here
    const selectedFile = event.target.files[0];
    console.log(selectedFile);
    setStartupImage(URL.createObjectURL(selectedFile));

    const startTime = performance.now();

    try {
      const formData = new FormData();
      formData.append(`startup_entity[image]`, selectedFile);
      const response = await fetchApi(
        `/startup_entities/${slug}`,
        "PATCH",
        formData,
        true
      );

      const endTime = performance.now(); // End timer for image upload
      const responseTime = Math.round(endTime - startTime);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Success:", responseData);
        Mixpanel.content("Project Image", "Updated", {
          content_id: slug,
          response_time_ms: responseTime,
          status: "success",
          startup_slug: slug,
          user_role: isOwner ? "owner" : "member",
        });
      } else {
        console.error("HTTP error! Failed to submit form");
        Mixpanel.error("Startup Image Upload Error", {
          error_message: errorData.message || "Failed to update image via API",
          error_type: "API Error",
          http_status: response.status,
          component: "StartupHeader",
          user_action: "Upload Startup Image",
          startup_slug: slug,
          response_time_ms: responseTime,
          user_role: isOwner ? "owner" : "member",
        });
      }
    } catch (error) {
      console.error("Error:", error.message);
      const endTime = performance.now(); // End timer even on network error
      const responseTime = Math.round(endTime - startTime);
      console.error("Error:", error.message);
      Mixpanel.error("Startup Image Upload Error", {
        error_message: error.message,
        error_type: "Network/Client Error",
        component: "StartupHeader",
        user_action: "Upload Startup Image",
        startup_slug: slug,
        response_time_ms: responseTime,
        user_role: isOwner ? "owner" : "member",
      });
    }
  };
  const handleClone = async () => {
    if (isCloning) return;
    setIsCloning(true);
    try {
      Mixpanel.button("Clone Project Button", {
        location: "Startup Header Action Buttons",
        startup_slug: slug,
      });

      const response = await fetchApi(`/startup_entities/${startup.slug}/clone`, "POST");

      if (!response.ok) {
        throw new Error("Failed to clone project");
      }

      const data = await response.json();
    
      Mixpanel.content("Project Clone", "Created", {
        content_id: data.slug,
        source_startup_slug: startup.slug,
      });

      window.location.href = `/projects/${data.slug}`;
    } catch (error) {
      console.log(error);
      Mixpanel.error("Project Clone Error", {
        error_message: error.message,
        source_startup_slug: startup.slug,
      });
      alert("Failed to clone project. Please try again.");
    } finally {
      setIsCloning(false);
    }
  };

  // Submit Project Functions
  const handleProjectSubmit = async () => {
    setProjectSubmitLoading(true);
    try {
      const payload = {
        is_submitted: !isSubmitted,
      };
      const response = await fetchApi(`/startup_entities/${slug}/submit_project`, 'POST', payload);
      if (response.ok) {
        const data = await response.json();
        setIsSubmitted(!isSubmitted);
        setSubmittedAt(data.submitted_at);
      } else {
        console.log("error in submitting project");
      }
    } catch (error) {
      console.log("error in submitting project", error);
    } finally {
      setProjectSubmitLoading(false);
    }
  };

  const handleSubmitChange = async (val) => {
    setSubmitValue(val);
    setSubmitData([]);

    if (!val.trim().length || val.trim().length < 3) {
      setSubmitLoading(false);
    } else {
      setSubmitLoading(true);
      try {
        const response = await fetch(`/api/users/search?search=${val}`);
        const users = await response.json();
        setSubmitData(users);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setSubmitLoading(false);
      }
    }
  };

  const handleSubmitConfirm = async () => {
    try {
      if (selectedSubmitUser) {
        await addProfessorToStartup(selectedSubmitUser);
      }
      await handleProjectSubmit();
    } catch (error) {
      console.log("error in submitting project ", error);
    } finally {
      closeSubmitProject();
      setSubmitValue("");
      setSelectedSubmitUser(null);
      setSubmitData([]);
    }
  };

  const addProfessorToStartup = async (user) => {
    try {
      const payload = {
        user_id: user.id,
        team_role: 5
      };
      const response = await fetchApi(`/startup_entities/${slug}/add_professor`, 'POST', payload);
      if (!response.ok) {
        console.log("error in adding professor");
      }
    } catch (error) {
      console.log("error in adding professor ", error);
    }
  };

  const resetSubmitFields = () => {
    setSubmitValue("");
    setSelectedSubmitUser(null);
    setSubmitData([]);
  };
  return (
    <ModalProvider>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="image/*"
      />
      {/* <Breadcrumbs
        links={[
          { name: "Home", href: "/dashboard/#/", active: false },
          { name: startup.name, active: true },
        ]}
      /> */}
      <CompactHeader>
        <CompactLeft>
          <Indicator
            color="indigo"
            offset={3}
            className="cursor-pointer"
            position="bottom-start"
            label={<IconPencil size={14} />}
            size={24}
            onClick={handleImageClick}
          >
            <CompactAvatar
              src={
                startupImage ||
                startup.image?.url ||
                "https://flowbite.com/docs/images/examples/image-3@2x.jpg"
              }
              alt="Startup"
            />
          </Indicator>
                      <CompactInfo>
              <CompactNameLocation>
                <CompactName>{startup.name}</CompactName>
                {startup.city && startup.country && (
                  <CompactLocation>
                    <LocationIcon src={locationIcon} alt="Location" />
                    {`${startup.city}, ${startup.country}`}
                  </CompactLocation>
                )}
              </CompactNameLocation>
            <CompactMeta>{startup.stage} • {startup.industry}</CompactMeta>
            <DescriptionContainer>
              <CompactDescription>
                {startup.description && startup.description.length > 120 && !isDescriptionExpanded
                  ? `${startup.description.substring(0, 120)}...`
                  : startup.description
                }
              </CompactDescription>
              {startup.description && startup.description.length > 120 && (
                <SeeMoreButton onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}>
                  {isDescriptionExpanded ? 'See less' : 'See more'}
                </SeeMoreButton>
              )}
            </DescriptionContainer>
          </CompactInfo>
        </CompactLeft>
        <CompactActions>
          <ActionButtonsRow>
            {((isOwner || role < 1) && !isSubmitted) && (
              <Tooltip label="Edit project" withArrow color="#6c45e7">
                <ActionButton aria-label="Edit project">
                  <a
                    href={`/projects/${slug}/edit`}
                    onClick={() =>
                      Mixpanel.button("Edit Project Button", {
                        location: "Startup Header",
                        startup_slug: slug,
                      })
                    }
                  >
                    <img src={editIcon} alt="Edit" />
                  </a>
                </ActionButton>
              </Tooltip>
            )}
            <Tooltip label="Copy link" withArrow color="#6c45e7">
              <ActionButton
                aria-label="Copy link"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  Mixpanel.share("Project Link", "Clipboard", {
                    content_id: slug,
                    share_method: "copy_link",
                    startup_name: startup.name,
                    page_url: window.location.href,
                  });
                }}
                style={{ cursor: "pointer" }}
              >
                <img src={shareIcon} alt="Share" />
              </ActionButton>
            </Tooltip>
            {isOwner&&
            <Tooltip label={isCloning ? "Cloning..." : "Clone project"} withArrow color="#6c45e7">
              <ActionButton aria-label={isCloning ? "Cloning..." : "Clone project"} onClick={handleClone} disabled={isCloning} aria-busy={isCloning}>
                {isCloning ? <Loader size="xs" color="#6c45e7" /> : <IconCopyPlus size={15} />}
              </ActionButton>
            </Tooltip>}
                            <ContainerButton
                  buttonText={
                    follow
                      ? t("startups_page.following")
                      : t("startups_page.follow")
                  }
                  action={async () => {
                    const newFollowState = await handleFollowUnfollow(follow, slug);
                    setFollow(newFollowState);
                    Mixpanel.button(newFollowState ? 'Follow Button' : 'Unfollow Button', {
                      category: 'Startup Engagement',
                      action: newFollowState ? 'followed' : 'unfollowed',
                      startup_slug: slug,
                      startup_name: startup.name,
                      initial_state: !newFollowState ? 'following' : 'not_following'
                    });
                  }}
                />
                <MemoContactMessage
                  slug={slug}
                  isMember={isOwner || isMember}
                  role={role}
                />
            <Menu shadow="md" width={220} closeOnItemClick={false}>
              <Menu.Target>
                <MenuButton>
                  <IconDotsVertical size={16} />
                </MenuButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Team & Communication</Menu.Label>
                {!(isMember || isOwner) ? (
                  <>
                    <Menu.Item 
                      icon={<IconUserPlus size={14} />}
                      onClick={() => {
                        window.dispatchEvent(new Event('open-invite-members'));
                      }}
                    >
                      {t("startups_page.members.request_join")}
                    </Menu.Item>
                    <Menu.Item 
                      icon={<IconCalendar size={14} />}
                      onClick={openMeeting}
                    >
                      {t("startups_page.members.request_meeting")}
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    <Menu.Item 
                      icon={<IconMessage size={14} />}
                      onClick={() => {
                        setOpenChat(true);
                        Mixpanel.feature('Chat', 'Opened', { source: 'Compact Header Menu' });
                      }}
                    >
                      {t("startups_page.members.open_chat")}
                    </Menu.Item>
                    <Menu.Item 
                      icon={<IconUsersGroup size={14} />}
                      onClick={fetchTeamData}
                    >
                      {t("startups_page.members.manage_team")}
                    </Menu.Item>
                    <Menu.Item 
                      icon={<IconDoor size={14} />}
                      onClick={openLeaveProject}
                    >
                      Leave Project
                    </Menu.Item>
                    <Menu.Item 
                      icon={<IconCalendar size={14} />}
                      onClick={openMeeting}
                    >
                      {t("startups_page.members.schedule")}
                    </Menu.Item>
                    {(isOwner || isMember) && (
                      <Menu.Item 
                        icon={<IconCheck size={14} />}
                        onClick={openSubmitProject}
                        disabled={isSubmitted}
                      >
                        {isSubmitted ? "Submitted" : "Submit Project"}
                      </Menu.Item>
                    )}
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          </ActionButtonsRow>
          <CompactMembers>
            {members.slice(0, 8).map((member, index) => {
              const initials = `${member.first_name ? member.first_name[0] : ""}${
                member.last_name ? member.last_name[0] : ""
              }`;
              return (
                <a key={member.id} href={`/profile/user/${member.id}`}>
                  {member.avatar_url ? (
                    <MemberAvatar
                      src={member.avatar_url}
                      alt={`Member ${index + 1}`}
                    />
                  ) : (
                    <MemberInitials>{initials}</MemberInitials>
                  )}
                </a>
              );
            })}
            {(isMember || isOwner) && members.length < 15 && (
              <InviteMembers />
            )}
          </CompactMembers>
        </CompactActions>
      </CompactHeader>

      {isDetailsOpen && (
      <StartupHeaderContainer>
        <StartupWrapper>
          <Indicator
            color="indigo"
            offset={5}
            className="cursor-pointer"
            position="bottom-start"
            label={<IconPencil size={16} />}
            size={30}
            onClick={handleImageClick}
          >
            <StartupImage
              src={
                startupImage ||
                startup.image?.url ||
                "https://flowbite.com/docs/images/examples/image-3@2x.jpg"
              }
              alt="Startup"
            />
          </Indicator>

          <StartupContent>
            <StartupTitle>{startup.name}</StartupTitle>
            {startup.description.length > 100 ? (
              <div>
                {" "}
                <StartupDescription>{truncatedDescription}</StartupDescription>
                <ReadMoreButton onClick={toggleCollapse}>
                  {isCollapsed ? "Read more" : "Read less"}
                </ReadMoreButton>
              </div>
            ) : (
              <StartupDescription>{startup.description}</StartupDescription>
            )}

            <ActionButtons>
              {((isOwner || role < 1) && !isSubmitted) && (
                <Tooltip label="Edit project" withArrow color="#6c45e7">
                  <ActionButton aria-label="Edit project">
                  <a
                    href={`/projects/${slug}/edit`}
                    onClick={() =>
                      Mixpanel.button("Edit Project Button", {
                        location: "Startup Header Action Buttons",
                        startup_slug: slug,
                        user_role: isOwner
                          ? "owner"
                          : role < 1
                          ? "admin"
                          : "other",
                      })
                    }
                  >
                    <img src={editIcon} alt="Edit" />
                  </a>
                </ActionButton>
                </Tooltip>
              )}
              <Tooltip label="Copy link" withArrow color="#6c45e7">
              <ActionButton
                  aria-label="Copy link"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert("Link copied to clipboard!");
                  Mixpanel.share("Project Link", "Clipboard", {
                    content_id: slug, // Using slug as content ID
                    share_method: "copy_link",
                    startup_name: startup.name,
                    page_url: window.location.href,
                  });
                }}
                style={{ cursor: "pointer" }}
              >
                <img src={shareIcon} alt="Share" />
              </ActionButton>
              </Tooltip>
              {isOwner&&
              <Tooltip label={isCloning ? "Cloning..." : "Clone project"} withArrow color="#6c45e7">
                <ActionButton aria-label={isCloning ? "Cloning..." : "Clone project"} onClick={handleClone} disabled={isCloning} aria-busy={isCloning}>
                  {isCloning ? <Loader size="xs" color="#6c45e7" /> : <IconCopyPlus size={15} />}
                </ActionButton>
              </Tooltip>}
              
            </ActionButtons>
          </StartupContent>
        </StartupWrapper>

        <StartupInfoHead>
          <InfoItem>
            <InfoItemLeft>
              <img src={locationIcon} alt="Location" />
              <b>{t("startups_page.location")}</b>
            </InfoItemLeft>
            <span>{`${startup.city} ${startup.country}`}</span>
          </InfoItem>
          <InfoItem>
            <InfoItemLeft>
              <img src={ideaIcon} alt="Idea" />
              <b>{t("startups_page.idea")}</b>
            </InfoItemLeft>
            <span>{startup.stage}</span>
          </InfoItem>
          <InfoItem>
            <InfoItemLeft>
              <img src={domainIcon} alt="Domain" />
              <b>{t("startups_page.domain")}</b>
            </InfoItemLeft>
            <span>{startup.industry}</span>
          </InfoItem>
          <div style={{ display: "flex", gap: "8px" }}>
            <ContainerButton
              buttonText={
                follow
                  ? t("startups_page.following")
                  : t("startups_page.follow")
              }
              action={async () => {
                const newFollowState = await handleFollowUnfollow(follow, slug);
                setFollow(newFollowState);
                Mixpanel.button(newFollowState ? 'Follow Button' : 'Unfollow Button', {
                  category: 'Startup Engagement',
                  action: newFollowState ? 'followed' : 'unfollowed',
                  startup_slug: slug,
                  startup_name: startup.name,
                  initial_state: !newFollowState ? 'following' : 'not_following'
                });
              }}

            />
            <MemoContactMessage
              slug={slug}
              isMember={isOwner || isMember}
              role={role}
            />
          </div>
        </StartupInfoHead>

        <TeamButton>
          <StartupMembers />
        </TeamButton>
      </StartupHeaderContainer>
      )}

      {(isMember || isOwner) && openChat && (
        <ChatComponent startupId={slug} setOpenChat={setOpenChat} />
      )}

      {/* Manage Team Modal */}
      <Modal
        opened={manageTeamOpened}
        onClose={closeManageTeam}
        size="xl"
        withCloseButton={false}
      >
        {notification && <Notification {...notification} />}
        <TextInput
          placeholder="Search by name or email"
          icon={<IconSearch size={16} />}
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.currentTarget.value)}
          mb={12}
        />
        <Tabs defaultValue="pending" color="violet" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="pending">{t("startups_page.manage_team.pending")}</Tabs.Tab>
            <Tabs.Tab value="members">{t("startups_page.manage_team.all")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="pending" pt="xs">
            <Card radius="md">
              <div className="flow-root">
                <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700 list-none">
                  {users.pending_requests
                    .filter(({ user }) => {
                      const q = teamSearch.toLowerCase();
                      if (!q) return true;
                      return (
                        `${user.first_name} ${user.last_name}`.toLowerCase().includes(q) ||
                        (user.email || "").toLowerCase().includes(q)
                      );
                    })
                    .map(({ user }, i) => (
                      <li className="py-3 sm:py-4" key={user.email}>
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            {user.avatar_url ? (
                              <Avatar src={user.avatar_url} mx="auto" className="w-8 h-8 rounded-full" />
                            ) : (
                              <Avatar mx="auto" className="w-8 h-8 rounded-full">{`${user.first_name[0]}${user.last_name[0]}`}</Avatar>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <a href={`/profile/${user.id}`}>
                              <p className="text-sm font-medium text-gray-900 truncate dark:text-white">{`${user.first_name} ${user.last_name}`}</p>
                              <p className="text-sm text-gray-500 truncate dark:text-gray-400">{user.email}</p>
                            </a>
                          </div>
                          <div className="inline-flex items-center text-base font-semibold text-gray-900 dark:text-white">
                            <Group gap={0} justify="flex-end">
                              <ActionIcon variant="default" size="xl" onClick={() => handleRequest(true, i)}>
                                <IconCheck size={22} stroke={1.5} />
                              </ActionIcon>
                              <ActionIcon variant="default" size="xl" onClick={() => handleRequest(false, i)}>
                                <IconX size={22} stroke={1.5} color="red" />
                              </ActionIcon>
                            </Group>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="members" pt="xs">
            <Card radius="md">
              <div className="flow-root">
                <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700 list-none">
                  {users.not_pending_requests
                    .filter(({ user }) => {
                      const q = teamSearch.toLowerCase();
                      if (!q) return true;
                      return (
                        `${user.first_name} ${user.last_name}`.toLowerCase().includes(q) ||
                        (user.email || "").toLowerCase().includes(q)
                      );
                    })
                    .map(({ user }, i) => (
                      <li className="py-3 sm:py-4" key={user.email}>
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            {user.avatar_url ? (
                              <Avatar src={user.avatar_url} mx="auto" className="w-8 h-8 rounded-full" />
                            ) : (
                              <Avatar mx="auto" className="w-8 h-8 rounded-full">{`${user.first_name[0]}${user.last_name[0]}`}</Avatar>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <a href={`/profile/user/${user.id}`}>
                              <p className="text-sm font-medium text-gray-900 truncate dark:text-white">{`${user.first_name} ${user.last_name}`}</p>
                              <p className="text-sm text-gray-500 truncate dark:text-gray-400">{user.email}</p>
                            </a>
                          </div>
                          <div className="inline-flex items-center text-base font-semibold text-gray-900 dark:text-white">
                            <Group gap={8} justify="flex-end">
                              <Select
                                placeholder="Role"
                                size="xs"
                                value={String(user.role)}
                                onChange={(val) => handleRole(user.membership_id, Number(val), user)}
                                data={[
                                  { value: '0', label: 'Founding Team' },
                                  { value: '1', label: 'Member' },
                                  { value: '2', label: 'Intern/Volunteer' },
                                  { value: '3', label: 'Consultant' },
                                  { value: '4', label: 'Investor' },
                                  { value: '5', label: 'Professor' },
                                ]}
                                withinPortal
                              />
                            </Group>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Modal>

      {/* Leave Project Modal */}
      <Modal
        opened={leaveProjectOpened}
        onClose={closeLeaveProject}
        size="lg"
        radius="md"
        overlayProps={{ blur: 3 }}
        withCloseButton
        title={
          <Group spacing={6}>
            <IconDoor size={18} />
            <Text fw={700} size="lg">Leave Project</Text>
          </Group>
        }
      >
        <Stack spacing="md">
          {notification && <Notification {...notification} />}

          <Card withBorder shadow="xs" radius="md" padding="md" style={{ background: "#FFF5F5", borderColor: "#FFE3E3" }}>
            <Text size="sm" color="red">
              Leaving this project will remove your access. This action cannot be undone.
            </Text>
          </Card>

          {isOwner ? (
            <Stack spacing={8}>
              <Text fw={600} size="md">You are the owner</Text>
              <Text size="sm" color="dimmed">Select a new owner to transfer ownership before leaving.</Text>
              <Select
                placeholder="Select member"
                data={members
                  .filter((member) => member.id !== currentUser.id)
                  .map((member) => ({
                    value: String(member.id),
                    label: `${member.first_name} ${member.last_name} (${member.email})`,
                  }))}
                value={selectedNewOwner}
                onChange={setSelectedNewOwner}
              />
            </Stack>
          ) : (
            <Text size="md">You are about to leave this {entity.toLowerCase()}. You will lose all member access.</Text>
          )}

          <Divider my="xs" />

          <Group justify="space-between" align="center">
            <Text size="sm" color="dimmed">This will immediately revoke your permissions.</Text>
            <Group>
              <Button variant="subtle" onClick={closeLeaveProject}>Cancel</Button>
              <Button
                color="red"
                variant="filled"
                onClick={async () => {
                  try {
                    // Clear previous notifications
                    setNotification(null);

                    // Owners must select a new owner before leaving
                    const body = isOwner ? { new_owner_id: Number(selectedNewOwner) } : {};

                    const response = await fetchApi(
                      `/startup_entities/${slug}/remove_member`,
                      'DELETE',
                      body
                    );

                    if (response.ok) {
                      // On success, user loses access to the project; redirect to dashboard
                      const data = await response.json().catch(() => ({}));
                      setNotification({ type: 'success', content: data.message || 'Left project successfully.' });
                      closeLeaveProject();
                      window.location.assign('/dashboard');
                    } else {
                      const errorData = await response.json().catch(() => ({}));
                      setNotification({
                        type: 'failure',
                        content: (errorData.errors && errorData.errors[0]) || errorData.error || 'Failed to leave project.'
                      });
                    }
                  } catch (err) {
                    setNotification({ type: 'failure', content: err?.message || 'An unexpected error occurred.' });
                  }
                }}
                disabled={isOwner && !selectedNewOwner}
              >
                Leave Project
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Hidden global instance to allow opening InviteMembers from anywhere (e.g., Menu) */}
      <div style={{ display: "none" }}>
        <InviteMembers />
      </div>
      
      {/* Meeting Modal */}
      <Modal
        opened={meetingOpened}
        onClose={closeMeeting}
        size="lg"
        radius="md"
        transitionProps={{ transition: "fade", duration: 200 }}
        withCloseButton={true}
      >
        <Stack spacing={18} className="p-5">
          {meetingNotification && <Notification {...meetingNotification} />}
          <Text size="lg" fw={500}>{t("startups_page.meeting.header")}</Text>
          <Text size="md">{t("startups_page.meeting.desc")}</Text>
          <TextInput
            size="md"
            label={t("startups_page.meeting.title")}
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          <Group grow>
            <label>Meeting Date:</label>
            <DatePickerComponent
              placeholder="Select Meeting Date"
              onChange={handleDateChange}
              min={new Date()}
            />
          </Group>

          <Group grow>
            <label>Start Time:</label>
            <TimePickerComponent
              placeholder="Select Start Time"
              enabled={!!meetingDate}
              onChange={handleStartTimeChange}
            />
          </Group>
          <Group grow>
            <label>End Time:</label>
            <TimePickerComponent
              ref={endTimeRef}
              placeholder="Select End Time"
              min={startDateTime}
              enabled={!!startDateTime}
            />
          </Group>
          <div onClick={(e) => e.stopPropagation()}>
            <MultiSelect
              data={memberNames}
              label="Select Members"
              placeholder="Choose people to invite"
              value={selectedValues}
              onChange={(values) => {
                setSelectedValues(values);
              }}
              withinPortal
            />
          </div>

          <Textarea
            size="md"
            radius="md"
            label={t("startups_page.meeting.agenda")}
            placeholder={t("startups_page.meeting.agenda_p")}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />

          <Button
            onClick={handleSchedule}
            variant="default"
            fullWidth
            className="w-full px-12"
            disabled={!(title && description && startDateTime)}
          >
            {(isMember || isOwner)
              ? t("startups_page.meeting.buttonText_m")
              : t("startups_page.members.request_meeting")}
          </Button>
        </Stack>
      </Modal>

      {/* Submit Project Modal */}
      <Modal
        opened={submitProjectOpened}
        onClose={() => {
          closeSubmitProject();
          resetSubmitFields();
        }}
        title={
          <Stack spacing={2}>
            <Group spacing={6}>
              <IconChecklist size={18} />
              <Text fw={700} size="lg">Submit Project</Text>
            </Group>
            <Text size="sm" color="dimmed">Review your team, add missing professors, and confirm submission.</Text>
          </Stack>
        }
        size="lg"
        radius="md"
        overlayProps={{ blur: 3 }}
      >
        <Stack spacing="md">
          <Group spacing="xs">
            <Badge color="violet" size="md">Professors</Badge>
            <Badge variant="outline" size="md">{submitProfessors.length} selected</Badge>
          </Group>

          <Tabs defaultValue="team" color="violet" keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="team">Team</Tabs.Tab>
              <Tabs.Tab value="add">Add Professor</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="team" pt="xs">
              <Card withBorder shadow="xs" radius="md" padding="md">
                <ScrollArea style={{ maxHeight: 320 }} type="auto">
                  {submitProfessors.length > 0 ? (
                    <Stack spacing="xs">
                      {submitProfessors.map((prof) => (
                        <Group key={prof.id} spacing="sm" style={{ padding: 6, border: "1px solid #F0F1F3", borderRadius: 6 }}>
                          <Avatar src={prof.avatar_url} size="sm" radius="xl">
                            {!prof.avatar_url && `${prof.first_name?.[0] || ""}${prof.last_name?.[0] || ""}`}
                          </Avatar>
                          <div>
                            <Text size="md" fw={500}>{prof.first_name} {prof.last_name}</Text>
                            <Text size="sm" color="dimmed">{prof.email}</Text>
                          </div>
                        </Group>
                      ))}
                    </Stack>
                  ) : (
                    <Box p="lg" style={{ border: "1px dashed #E3E6EA", borderRadius: 8, textAlign: "center" }}>
                      <Text size="md" color="dimmed">No professors added yet. Switch to "Add Professor" to invite.</Text>
                    </Box>
                  )}
                </ScrollArea>
              </Card>
            </Tabs.Panel>

            <Tabs.Panel value="add" pt="xs">
              <Card withBorder shadow="xs" radius="md" padding="md">
                {!selectedSubmitUser ? (
                  <TextInput
                    icon={<IconSearch size={16} />}
                    variant="default"
                    placeholder="Search by name or email"
                    value={submitValue}
                    onChange={(event) => handleSubmitChange(event.currentTarget.value)}
                    rightSection={submitLoading ? <Loader size="1rem" /> : null}
                  />
                ) : (
                  <Box style={{ position: "relative", border: "1px solid #EEF1F4", borderRadius: 8, padding: 10, background: "#FAFBFC", minHeight: 48 }}>
                    <Button variant="subtle" size="xs" style={{ position: "absolute", top: 6, right: 6 }} onClick={() => setSelectedSubmitUser(null)}>
                      <IconX size={14} />
                    </Button>
                    <Group>
                      <Avatar src={selectedSubmitUser.avatar_url} radius="xl" size="sm">
                        {!selectedSubmitUser.avatar_url && `${selectedSubmitUser.first_name?.[0] || ""}${selectedSubmitUser.last_name?.[0] || ""}`}
                      </Avatar>
                      <div>
                        <Text size="md" fw={600}>{selectedSubmitUser.first_name} {selectedSubmitUser.last_name}</Text>
                        <Text color="dimmed" size="sm">{selectedSubmitUser.email}</Text>
                      </div>
                    </Group>
                  </Box>
                )}

                {!selectedSubmitUser && submitValue.trim().length > 0 && !submitLoading ? (
                  submitData.length > 0 ? (
                    <ScrollArea style={{ maxHeight: 260, marginTop: 8 }} type="auto">
                      <Stack spacing={6}>
                        {submitData.map((user) => (
                          <Group
                            key={user.id}
                            spacing="sm"
                            style={{ padding: 8, border: "1px solid #F0F1F3", borderRadius: 6, cursor: "pointer", transition: "background 150ms" }}
                            onClick={() => setSelectedSubmitUser(user)}
                          >
                            <Avatar src={user.avatar_url} radius="xl" size="sm">
                              {!user.avatar_url && `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`}
                            </Avatar>
                            <div>
                              <Text size="md" fw={500}>{user.first_name} {user.last_name}</Text>
                              <Text color="dimmed" size="sm">{user.email}</Text>
                            </div>
                          </Group>
                        ))}
                      </Stack>
                    </ScrollArea>
                  ) : (
                    <Box p="sm" mt={8} style={{ border: "1px solid #eee", borderRadius: 6, textAlign: "center" }}>
                      <Text size="md">No results for "{submitValue}"</Text>
                    </Box>
                  )
                ) : null}
              </Card>
            </Tabs.Panel>
          </Tabs>

          <Divider my="xs" />

          <Group justify="space-between" align="center">
            <Text size="sm" color="dimmed">Submission is final and cannot be undone.</Text>
            <Group>
              <Button variant="subtle" color="red" onClick={() => closeSubmitProject()}>Cancel</Button>
              <Tooltip
                label="You need to have at least one professor before submitting the project"
                color="violet"
                disabled={!(submitProfessors.length === 0 && !selectedSubmitUser)}
                withArrow
                position="bottom"
              >
                <div>
                  <Button
                    color="violet"
                    onClick={handleSubmitConfirm}
                    disabled={isSubmitted || (submitProfessors.length === 0 && !selectedSubmitUser)}
                    loading={projectSubmitLoading}
                  >
                    Confirm & Submit
                  </Button>
                </div>
              </Tooltip>
            </Group>
          </Group>
        </Stack>
      </Modal>
      
    </ModalProvider>
  );
};

export default StartupHeader;
